import { useMemo } from 'react';

import { ASSET_TYPES } from '@/shared/api/openmetadata';
import { formatDateTime } from '@/shared/lib/format/dateTime';
import { formatBytes, formatMetricValue } from '@/shared/lib/utils/number';
import type { BadgeItem } from '@/shared/types/badge';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { BadgeList } from '@/shared/ui/BadgeList';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import type { DrawerSection, InfoRow } from '@/shared/ui/lineage/LineageNodeDrawer';

import { RUN_RELATED_TYPES } from '@/entities/asset-search';
import type { SearchResult } from '@/entities/search';

import styles from './useAssetSearchPreview.module.css';

type UseAssetSearchPreviewProps = {
  selectedResult: SearchResult | null;
};

function mapColumnsToBadgeItems(columns: Array<string | { name: string }> | undefined): BadgeItem[] {
  return (columns || []).map((col) => {
    const colName = typeof col === 'string' ? col : col.name;
    return { name: colName, tagFQN: colName };
  });
}

function AssetPreviewKeyValueTable({
  headerLeft,
  headerRight,
  rows,
}: {
  headerLeft: string;
  headerRight: string;
  rows: Array<{ key: string; left: string; right: string }>;
}) {
  if (rows.length === 0) return null;

  return (
    <div className={styles.previewKvInner}>
      <div className={styles.previewKvSection}>
        <div className={styles.previewKvHeader}>
          <span className={styles.previewKvHeaderLabel}>{headerLeft}</span>
          <span className={styles.previewKvHeaderLabel}>{headerRight}</span>
        </div>
        <div className={styles.previewKvBody}>
          {rows.map((row) => (
            <div key={row.key} className={styles.previewKvRow}>
              <span className={styles.previewKvLabel}>
                <EllipsisText text={row.left} tooltipPlacement="topLeft" />
              </span>
              <span className={styles.previewKvValue}>
                <EllipsisText text={row.right} tooltipPlacement="topRight" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AssetPreviewDatasetFeatureSection({
  description,
  table,
  columnItems,
  creator,
  createdAt,
  updatedAt,
}: {
  description?: string;
  table: string;
  columnItems: BadgeItem[];
  creator?: string;
  createdAt?: string;
  updatedAt?: string;
}) {
  return (
    <div className={styles.sectionContent}>
      <div className={styles.nameTitle}>
        <EllipsisText text={table}>{table}</EllipsisText>
      </div>

      {description && (
        <div className={styles.metaDescriptionSection}>
          <AssetDescription description={description} mode="expandable" />
        </div>
      )}

      <div className={styles.nameInfo}>
        <div className={styles.nameInfoRow}>
          <span className={styles.nameInfoLabel}>소유자</span>
          <span className={styles.nameInfoValue}>{creator || '-'}</span>
        </div>
        <div className={styles.nameInfoRow}>
          <span className={styles.nameInfoLabel}>생성일</span>
          <span className={styles.nameInfoValue}>{createdAt ? formatDateTime(createdAt) : '-'}</span>
        </div>
        <div className={styles.nameInfoRow}>
          <span className={styles.nameInfoLabel}>수정일</span>
          <span className={styles.nameInfoValue}>{updatedAt ? formatDateTime(updatedAt) : '-'}</span>
        </div>
      </div>

      {columnItems.length > 0 && (
        <div className={styles.metaColumnsSection}>
          <div className={styles.metaColumnsTitle}>컬럼</div>
          <BadgeList variant="column" items={columnItems} showIcon={false} />
        </div>
      )}
    </div>
  );
}

export const useAssetSearchPreview = ({ selectedResult }: UseAssetSearchPreviewProps) => {
  const rawAssetItem = selectedResult?._source?.rawAssetItem;
  const isAssetSearch = rawAssetItem && RUN_RELATED_TYPES.some((t) => t === selectedResult.type);

  const infoRows = useMemo((): InfoRow[] => {
    if (!selectedResult || !rawAssetItem || !isAssetSearch) return [];

    const rows: InfoRow[] = [];

    const ownerLabel =
      rawAssetItem.owner?.displayName?.trim() || rawAssetItem.owner?.name?.trim() || rawAssetItem.creator?.trim() || '';
    if (ownerLabel) {
      rows.push({
        label: '소유자',
        value: ownerLabel,
      });
    }

    if (rawAssetItem.createdAt) {
      rows.push({
        label: '생성일',
        value: formatDateTime(rawAssetItem.createdAt),
      });
    }

    if (rawAssetItem.updatedAt) {
      rows.push({
        label: '수정일',
        value: formatDateTime(rawAssetItem.updatedAt),
      });
    }

    return rows;
  }, [selectedResult, rawAssetItem, isAssetSearch]);

  const sections = useMemo((): DrawerSection[] => {
    if (!selectedResult || !rawAssetItem || !isAssetSearch) return [];

    const allSections: Record<string, DrawerSection> = {};

    const normalizeType = (type: string): string => {
      const typeMap: Record<string, string> = {
        run_artifact: 'artifact',
        run_dataset: 'dataset',
        run_feature_set: 'feature_set',
        run_parameter_set: 'parameter_set',
      };
      return typeMap[type] || type;
    };

    const currentType = normalizeType(selectedResult.type);

    if (rawAssetItem.dataset) {
      const ds = rawAssetItem.dataset;

      allSections['dataset'] = {
        id: 'dataset',
        title: '데이터셋',
        collapsible: true,
        content: (
          <AssetPreviewDatasetFeatureSection
            description={ds.description}
            table={ds.table}
            columnItems={mapColumnsToBadgeItems(ds.columns)}
            creator={ds.creator}
            createdAt={ds.createdAt}
            updatedAt={ds.updatedAt}
          />
        ),
      };
    }

    if (rawAssetItem.featureSet) {
      const fs = rawAssetItem.featureSet;

      allSections['feature_set'] = {
        id: 'feature_set',
        title: '피처셋',
        collapsible: true,
        content: (
          <AssetPreviewDatasetFeatureSection
            description={fs.description}
            table={fs.table}
            columnItems={mapColumnsToBadgeItems(fs.columns)}
            creator={fs.creator}
            createdAt={fs.createdAt}
            updatedAt={fs.updatedAt}
          />
        ),
      };
    }

    if (rawAssetItem.metrics && Object.keys(rawAssetItem.metrics).length > 0) {
      const metrics = Object.entries(rawAssetItem.metrics);
      const metricRows = metrics.map(([key, value]) => ({
        key,
        left: key,
        right: typeof value === 'number' ? formatMetricValue(value) : String(value),
      }));

      allSections['metrics'] = {
        id: 'metrics',
        title: '메트릭',
        collapsible: true,
        content: (
          <div className={styles.sectionContent}>
            <AssetPreviewKeyValueTable headerLeft="Metric" headerRight="Value" rows={metricRows} />
          </div>
        ),
      };
    }

    if (rawAssetItem.artifacts && rawAssetItem.artifacts.count > 0) {
      const files = rawAssetItem.artifacts.files || [];
      const onlyFiles = files.filter((file) => !file.isDir);
      const artifactRows = onlyFiles.map((file, index) => {
        const artifactsIndex = file.path.indexOf('/artifacts/');
        const displayPath =
          artifactsIndex !== -1 ? file.path.substring(artifactsIndex + '/artifacts/'.length) : file.path;
        return {
          key: `${index}-${file.path}`,
          left: displayPath,
          right: file.fileSize != null ? formatBytes(file.fileSize) : '—',
        };
      });

      allSections['artifact'] = {
        id: 'artifacts',
        title: '산출물',
        collapsible: true,
        content: (
          <div className={styles.sectionContent}>
            {rawAssetItem.artifacts.description && (
              <div className={styles.metaDescriptionSection}>
                <AssetDescription description={rawAssetItem.artifacts.description} mode="expandable" />
              </div>
            )}
            {artifactRows.length > 0 ? (
              <AssetPreviewKeyValueTable headerLeft="File" headerRight="Size" rows={artifactRows} />
            ) : (
              <div className={styles.emptyMessage}>파일이 없습니다</div>
            )}
          </div>
        ),
      };
    }

    if (
      selectedResult.type === ASSET_TYPES.RUN &&
      rawAssetItem.experimentTags &&
      rawAssetItem.experimentTags.length > 0
    ) {
      const tagItems: BadgeItem[] = rawAssetItem.experimentTags.map((tag) => ({
        tagFQN: tag,
        name: tag,
      }));

      allSections['tags'] = {
        id: 'tags',
        title: '태그',
        content: <BadgeList items={tagItems} iconName="tag" isEllipsis showTooltip />,
      };
    }

    const sectionOrder = ['dataset', 'feature_set', 'metrics', 'artifact', 'tags'];

    const orderedKeys =
      currentType === 'artifact' ? [currentType, ...sectionOrder.filter((key) => key !== currentType)] : sectionOrder;

    return orderedKeys.filter((key) => allSections[key]).map((key) => allSections[key]);
  }, [selectedResult, rawAssetItem, isAssetSearch]);

  const description = null;

  return {
    infoRows,
    sections,
    description,
    isLoading: false,
    isError: false,
    refetch: () => Promise.resolve(),
  };
};
